<template>
  <!--
    O RECIBO DA FOLHA (25/09/2026, migration 0060). O worker cobra a folha à noite, e até aqui
    cobrava em silêncio — ou, sem ouro, não cobrava nada. Agora cada noite deixa um recibo, e
    este modal mostra os que o jogador ainda não fechou: o que saiu do ouro, o que a empresa
    vendeu do estoque, que bem foi a leilão, e se a tripulação foi embora. O papel é o mesmo
    da Taxa do Porto, de propósito: é a outra conta do dia.
  -->
  <FishModal :aberto="true" :titulo="$t('folha.titulo')" :fechavel="false">
    <div class="folha">
      <section
        v-for="recibo in recibos"
        :key="recibo.id"
        class="folha__recibo"
        :class="`folha__recibo--${recibo.desfecho}`"
      >
        <header class="folha__cabecalho">
          <strong>{{ $t(`folha.desfecho.${recibo.desfecho}`) }}</strong>
          <span class="folha__dia">{{ formatarDia(recibo.dia) }}</span>
        </header>

        <ul class="folha__lancamentos">
          <li v-if="recibo.salarios > 0">
            <span><FishIcon nome="users" tamanho="miudo" /> {{ $t('folha.salarios') }}</span>
            <span class="t-numero">{{ numero(recibo.salarios) }}</span>
          </li>
          <li v-if="recibo.dividaAnterior > 0">
            <span><FishIcon nome="coins" tamanho="miudo" /> {{ $t('folha.dividaAnterior') }}</span>
            <span class="t-numero">{{ numero(recibo.dividaAnterior) }}</span>
          </li>
          <li v-if="recibo.estoqueVendido > 0" class="folha__credito">
            <span>
              <FishIcon nome="fish" tamanho="miudo" />
              {{ $t('folha.estoqueVendido', { n: recibo.estoqueVendido }) }}
            </span>
            <span class="t-numero">+{{ numero(recibo.ouroDoEstoque) }}</span>
          </li>
          <li v-for="(bem, i) in recibo.bensVendidos" :key="i" class="folha__credito">
            <span>
              <FishIcon :nome="ICONE_DO_BEM[bem.tipo] || 'coins'" tamanho="miudo" />
              {{ $t('folha.bemVendido', { nome: bem.nome }) }}
            </span>
            <span class="t-numero">+{{ numero(bem.valor) }}</span>
          </li>
          <li class="folha__pago">
            <span>{{ $t('folha.pago') }}</span>
            <span class="t-numero">-{{ numero(recibo.pago) }}</span>
          </li>
          <li v-if="recibo.dividaDepois > 0" class="folha__devendo">
            <span>{{ $t('folha.ficouDevendo') }}</span>
            <span class="t-numero">{{ numero(recibo.dividaDepois) }}</span>
          </li>
        </ul>

        <p v-if="recibo.funcionariosSairam > 0" class="folha__nota folha__nota--ruim">
          {{ $t('folha.sairam', { n: recibo.funcionariosSairam }) }}
        </p>
      </section>

      <p v-if="divida > 0" class="folha__nota folha__nota--ruim">
        {{ $t('folha.avisoDaDivida', { divida: numero(divida) }) }}
      </p>
    </div>

    <template #acoes>
      <FishButton variante="primario" bloco :carregando="fechando" @click="fechar">
        {{ $t('folha.fechar') }}
      </FishButton>
    </template>
  </FishModal>
</template>

<script>
import { FishButton, FishIcon, FishModal } from '@/shared/ui';
import * as v2 from '@/core/services/api/v2';

/** O ícone de cada bem que a folha pode vender. `home` é a casa: não existe `house` na folha de sprites. */
const ICONE_DO_BEM = { boat: 'boat', house: 'home', map: 'map' };

export default {
  name: 'FolhaModal',
  components: { FishButton, FishIcon, FishModal },
  props: {
    playerId: { type: Number, required: true },
    recibos: { type: Array, required: true },
    divida: { type: Number, default: 0 }
  },
  emits: ['close'],
  data() {
    return { ICONE_DO_BEM, fechando: false };
  },
  methods: {
    numero(n) {
      return new Intl.NumberFormat('pt-BR').format(n || 0);
    },
    /** `YYYY-MM-DD` (UTC, do servidor) vira `dd/mm` — o ano não ajuda a ler um recibo de ontem. */
    formatarDia(dia) {
      const [, mes, d] = String(dia).split('-');
      return d && mes ? `${d}/${mes}` : dia;
    },
    /**
     * Fecha e marca os recibos como vistos. Se a marcação falhar, fecha assim
     * mesmo: o pior caso é o recibo aparecer de novo no próximo login, e prender
     * o jogador num modal por causa disso seria pior.
     */
    async fechar() {
      if (this.fechando) return;
      this.fechando = true;
      try {
        await v2.fecharRecibosDaFolha(this.playerId);
      } catch (error) {
        console.error('Falha ao fechar os recibos da folha:', error.message);
      } finally {
        this.fechando = false;
        this.$emit('close');
      }
    }
  }
};
</script>

<style scoped>
.folha {
  display: flex;
  flex-direction: column;
  gap: var(--e-3);
  max-height: 60vh;
  overflow-y: auto;
}

/* Cada noite é um recibo: papel um degrau mais claro, como o da Taxa do Porto. */
.folha__recibo {
  background: var(--papel-luz);
  border: 2px solid var(--papel-sombra);
  padding: var(--e-2) var(--e-3);
  font-size: var(--f-2);
}

.folha__recibo--debt,
.folha__recibo--assetsSold,
.folha__recibo--employeesQuit {
  border-color: var(--cor-ruim);
}

.folha__cabecalho {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: var(--e-2);
  margin-bottom: var(--e-1);
  font-family: var(--fonte-display);
}

.folha__dia {
  color: var(--tinta-fraca);
  font-size: var(--f-1);
}

.folha__lancamentos {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--e-1);
}

.folha__lancamentos li {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--e-2);
  color: var(--tinta-fraca);
}

.folha__lancamentos li > span:first-child {
  display: inline-flex;
  align-items: center;
  gap: var(--e-1);
}

.folha__credito .t-numero {
  color: var(--ouro-escuro);
}

.folha__pago {
  padding-top: var(--e-1);
  border-top: 2px dashed var(--papel-sombra);
  color: var(--tinta) !important;
}

.folha__pago .t-numero,
.folha__devendo,
.folha__devendo .t-numero {
  color: var(--cor-ruim);
}

.folha__nota {
  margin: var(--e-2) 0 0;
  font-size: var(--f-1);
}

.folha__nota--ruim {
  color: var(--cor-ruim);
}
</style>
